import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import SidebarHeader from "./sub/SidebarHeader";
import MenuItem from "./sub/MenuItem";
import SupportHubvex from "./sub/SupportHubvex";

interface SidebarProps {
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const menuSections = [
  {
    title: "Principale",
    items: [{ name: "Dashboard", icon: "LayoutDashboard", path: "/admin/dashboard/home" }],
  },
  {
    title: "Contenu",
    items: [
      { name: "Catégories projets", icon: "FolderTree", path: "/admin/dashboard/project-categories" },
      { name: "Projets", icon: "Briefcase", path: "/admin/dashboard/projects" },
      { name: "Catégories produits", icon: "FolderTree", path: "/admin/dashboard/product-categories" },
      { name: "Produits", icon: "Package", path: "/admin/dashboard/products" },
      { name: "Services", icon: "Server", path: "/admin/dashboard/services" },
    ],
  },
  {
    title: "Commerce",
    items: [
      {
        name: "Commandes",
        icon: "ShoppingCart",
        path: "/admin/dashboard/commerces/orders",
      },
      {
        name: "Produits vendus",
        icon: "PackageCheck",
        path: "/admin/dashboard/commerces/product-stats",
      },
      {
        name: "Clients",
        icon: "Users",
        path: "/admin/dashboard/commerces/customers",
      },
    ],
  },

  {
    title: "Finances",
    items: [
      { name: "Transactions", icon: "ArrowLeftRight", path: "/admin/dashboard/finances/transactions" },
      { name: "Relevés", icon: "BarChart3", path: "/admin/dashboard/finances/reports" },
    ],
  },
  {
    title: "Devis",
    items: [
      { name: "Demandes de devis", icon: "FileText", path: "/admin/dashboard/devis-requests" },
      // Ajoutez d'autres sous-menus ici si nécessaire (ex: "Catégories devis")
    ],
  },
  {
    title: "Administration",
    items: [
      { name: "Utilisateurs", icon: "UserCog", path: "/admin/dashboard/users" },
      { name: "Notifications", icon: "Bell", path: "/admin/dashboard/admin-notifications" },
    ],
  },
  {
    title: "Compte",
    items: [
      { name: "Profil", icon: "User", path: "/admin/dashboard/profile" },
      { name: "Sécurité", icon: "Shield", path: "/admin/dashboard/security" },
      { name: "Préférences", icon: "BellRing", path: "/admin/dashboard/preferences" },
      { name: "Paramètres", icon: "Settings", path: "/admin/dashboard/settings" },
    ],
  }
];

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, onMobileClose }) => {
  return (
    <>
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onMobileClose}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-50 h-full w-[280px] 
          transition-transform duration-300 ease-in-out
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div
          className="relative h-full flex flex-col bg-dark/95 backdrop-blur-md border-t-8 border-l-8 border-secondary rounded-2xl"
          style={{
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(2, 202, 253, 0.1) inset,
              0 0 15px rgba(22, 241, 158, 0.2),
              20px 0 30px -15px rgba(0, 0, 0, 0.6),
              -5px 0 15px -5px rgba(2, 202, 253, 0.3)
            `,
          }}
        >
          <div className="flex h-full flex-col px-5 py-6 overflow-y-auto persoscrollbar scrollbar-thin scrollbar-thumb-white/10">
            <SidebarHeader />
            <nav className="mt-6 flex-1 space-y-4">
              {menuSections.map((section, idx) => (
                <div key={idx} className="relative">
                  {/* Titre de section (pas de trait ici) */}
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    {section.title}
                  </h3>

                  {/* Conteneur avec arborescence */}
                  <div className="relative ml-2">
                    {/* Trait vertical principal (continu) */}
                    <div className="absolute left-0 top-0 bottom-0 w-px bg-white/50 " />

                    <div className="space-y-1">
                      {section.items.map((item, itemIdx) => (
                        <div key={item.name} className="relative">
                          {/* Trait horizontal de connexion */}
                          <div className="absolute left-0 top-1/2 w-3 h-px bg-white/50" />
                          {/* MenuItem décalé à droite */}
                          <div className="pl-4">
                            <MenuItem item={item} onMobileClose={onMobileClose} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </nav>
            <SupportHubvex />
          </div>
        </div>

        <button
          onClick={onMobileClose}
          className="lg:hidden absolute top-4 right-4 z-50 bg-white/50 backdrop-blur p-2 rounded-full text-white hover:bg-white/20 hover:scale-105 transition-all duration-200 shadow-lg"
        >
          <X size={20} />
        </button>
      </aside>
    </>
  );
};

export default Sidebar;